import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import apiService from '../services/api';
import { TaskListItem, TaskMetaResponse } from '../types/task';
import TaskFormDialog from './TaskFormDialogV2';

type Props = {
  caseId: string;
};

const RelatedTasksBlock: React.FC<Props> = ({ caseId }) => {
  const [meta, setMeta] = useState<TaskMetaResponse | null>(null);
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    const [m, list] = await Promise.all([
      apiService.getTaskMeta().catch(() => null),
      apiService.listTasks({ page: 1, per_page: 50, case_id: caseId }).catch(() => ({ tasks: [], total: 0 })),
    ]);
    if (m) setMeta(m);
    setTasks(list.tasks || []);
  }, [caseId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const statusLabel = (code?: string | null) => meta?.statuses?.find((s) => s.code === code)?.name || code || '';

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Related tasks
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Add task
          </Button>
        </Stack>

        <Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Typography
                      component={RouterLink}
                      to={`/crm/tasks/${t.id}`}
                      sx={{ textDecoration: 'none', fontWeight: 600, color: 'primary.main' }}
                    >
                      {t.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={statusLabel(t.status_code)} variant="outlined" />
                  </TableCell>
                  <TableCell>{t.due_at ? new Date(t.due_at).toLocaleString() : '-'}</TableCell>
                </TableRow>
              ))}
              {tasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary">
                      No tasks linked to this case
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <TaskFormDialog
          open={createOpen}
          caseId={caseId}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            fetchAll();
          }}
        />
      </CardContent>
    </Card>
  );
};

export default RelatedTasksBlock;
